/**
 * The shared inline-markup reader: SRT's `<i>/<b>/<u>/<font>`, WebVTT's `<c>/<v>/<ruby>` and
 * cue timestamps, SAMI's `<br>` and `<font>`.
 *
 * Three parsers reading almost-the-same markup three times is three sets of entity-decoding
 * bugs and three answers to "is `<3` a tag?". It is one here, parameterised by what each
 * format actually allows.
 *
 * TWO RULES THAT DECIDE MOST OF THE BEHAVIOUR:
 *
 * 1. **`InlineStyle` is RESOLVED, not cumulative** (contract §5, frozen). Each run carries its
 *    COMPLETE effective delta from the cue's Style — run N is never interpreted against run
 *    N−1. So the open-tag stack is flattened into a full delta at every run boundary.
 *
 * 2. **An unrecognised `<…>` is TEXT, not a tag.** Subtitle dialogue contains `<` and `>`
 *    constantly ("<< PREVIOUS", "5 < 7", emoticons). A permissive tokenizer eats them and the
 *    user sees text vanish with no error, which is the worst available failure. Only the
 *    patterns each format actually defines are treated as markup.
 */

import type {
  Alignment,
  Annotation,
  Color,
  IdAllocator,
  InlineStyle,
  TextLine,
  TextRun,
  Ticks,
  TimeSpan,
} from '../../../contracts/cue-model.ts';
import { makeLine, makeRun } from '../../core/model/build.ts';
import { T } from '../../core/time/ticks.ts';
import { parseCssColor } from './color.ts';

export interface MarkupCaps {
  /** `<c.yellow.bg_blue>` class lists (WebVTT). */
  readonly classes: boolean;
  /** `<v Bob>` voice spans (WebVTT). */
  readonly voice: boolean;
  /** `<ruby>base<rt>reading</rt></ruby>` (WebVTT). */
  readonly ruby: boolean;
  /** `<00:00:01.000>` cue timestamps (WebVTT) — the per-word timing carrier. */
  readonly timestamps: boolean;
  /** `<br>` as a line break (SAMI). */
  readonly br: boolean;
  /** `<font color= face= size=>` (SRT, SAMI). */
  readonly font: boolean;
  /** The `{\an8}`, `{\i1}`, `{\b1}`, `{\u1}` subset that leaks into SRT from ASS sources. */
  readonly assTags: boolean;
}

export const SRT_MARKUP: MarkupCaps = {
  classes: false,
  voice: false,
  ruby: false,
  timestamps: false,
  br: false,
  font: true,
  assTags: true,
};

export const VTT_MARKUP: MarkupCaps = {
  classes: true,
  voice: true,
  ruby: true,
  timestamps: true,
  br: false,
  // WebVTT has no <font>; a file that carries one came from a careless SRT conversion, and
  // reading it costs nothing while losing it costs the user their colours.
  font: true,
  assTags: false,
};

export const SMI_MARKUP: MarkupCaps = {
  classes: false,
  voice: false,
  ruby: false,
  timestamps: false,
  br: true,
  font: true,
  assTags: false,
};

export interface MarkupInput {
  /** Cue text. A literal '\n' is a HARD line break — the source had one. */
  readonly text: string;
  readonly alloc: IdAllocator;
  readonly caps: MarkupCaps;
  readonly cueStart: Ticks;
  readonly cueEnd: Ticks;
  /** Parses a WebVTT cue timestamp. Injected so this module needs no dialect knowledge. */
  readonly parseTimestamp?: (s: string) => Ticks | null;
  /**
   * `<c>` classes that ARE colours (WebVTT: the §5.1 defaults and the file's own simple
   * `::cue(.name) { color }` rules, from `readVttColorClasses`). Such a class is read as the
   * run's `primaryColor` rather than kept as a class name, so a VTT colour survives a
   * conversion to SRT or ASS and a recolour replaces it instead of nesting inside it.
   */
  readonly classColors?: ReadonlyMap<string, Color>;
}

export interface MarkupOut {
  readonly lines: readonly TextLine[];
  /** From `{\anN}`; null when the text carried none. */
  readonly alignment: Alignment | null;
  /** True when at least one run carries a `<mm:ss.ttt>` timing — the cue is word-granular. */
  readonly wordTimed: boolean;
  /** Voice names seen, in order. WebVTT `<v Bob>` also feeds `Cue.actor` for the first one. */
  readonly voices: readonly string[];
  /** Markup that looked like a tag and was not one of ours. Reported, never silently eaten. */
  readonly unknown: readonly string[];
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Entities
───────────────────────────────────────────────────────────────────────────────────────*/

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  // SAMI files in the wild use these three constantly.
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
};

const ENTITY_RE = /&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;

/** Decodes the entity set these formats actually use. An unknown entity is left VERBATIM —
 *  turning `&foo;` into nothing would delete the user's text. */
export function decodeEntities(s: string): string {
  if (!s.includes('&')) return s;
  return s.replace(ENTITY_RE, (whole, body: string) => {
    if (body.startsWith('#')) {
      const hex = body[1] === 'x' || body[1] === 'X';
      const n = Number.parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return whole;
      // Surrogate halves are not scalar values; emitting one produces a lone surrogate that
      // breaks every downstream measure and every JSON round trip.
      if (n >= 0xd800 && n <= 0xdfff) return whole;
      return String.fromCodePoint(n);
    }
    return ENTITIES[body.toLowerCase()] ?? whole;
  });
}

/** The inverse, for writers. Only the three that MUST be escaped, plus the one that must not
 *  be a literal in SAMI. Over-escaping makes a diff of two exports unreadable. */
export function encodeEntities(s: string): string {
  return s.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The tokenizer
───────────────────────────────────────────────────────────────────────────────────────*/

interface OpenTag {
  readonly name: string;
  readonly delta: InlineStyle;
  readonly classes: readonly string[];
  readonly voice: string | null;
}

const ALIGN_RE = /^\{\\an?([1-9])\}$/;
const TOGGLE_RE = /^\{\\([ibu])([01])\}$/;
const TS_RE = /^(?:\d+:)?\d{1,2}:\d{1,2}[.,]\d{1,3}$/;

export function parseMarkup(input: MarkupInput): MarkupOut {
  const { text, alloc, caps } = input;
  const lines: TextLine[] = [];
  const unknown: string[] = [];
  const voices: string[] = [];
  let alignment: Alignment | null = null;
  let wordTimed = false;

  const stack: OpenTag[] = [];
  let runs: TextRun[] = [];
  let buf = '';
  let breakBefore: TextLine['breakBefore'] = 'none';
  /** Start of the run currently being accumulated, when a cue timestamp opened it. */
  let runStart: Ticks | null = null;
  /** Ruby state: while collecting an `<rt>` the text goes to the annotation, not the run. */
  let rubyBase: TextRun | null = null;
  let inRt = false;
  let rtBuf = '';

  const effective = (): { style: InlineStyle; voice: string | null } => {
    let style: InlineStyle = {};
    const classes: string[] = [];
    let voice: string | null = null;
    for (const t of stack) {
      style = { ...style, ...t.delta };
      classes.push(...t.classes);
      if (t.voice !== null) voice = t.voice;
    }
    if (classes.length > 0) style = { ...style, classes };
    if (voice !== null) style = { ...style, voice };
    return { style, voice };
  };

  const flushRun = (endTime: Ticks | null): void => {
    // AN EMPTY FLUSH EMITS NOTHING, SO A CUE TIMESTAMP MUST SURVIVE IT. Every tag flushes before
    // it opens, so `<00:00:02.000><b>word</b>` — and `<ruby>`, and `<c.loud>` — flushed an empty
    // buffer between the stamp and its text. Clearing `runStart` here threw that stamp away, and
    // the run came out untimed: word timing silently lost in the one format whose per-word
    // timestamps are a differentiator, for input as ordinary as a bolded word. Worse, one untimed
    // run makes the cue not fully timed, so the writer then dropped the SURVIVING stamps too.
    // `flushLine` clears it instead, so a stamp still cannot leak across a line break.
    if (buf === '') return;
    const { style } = effective();
    const time: TimeSpan | null =
      runStart === null ? null : { start: runStart, end: endTime ?? input.cueEnd };
    runs.push(
      makeRun({
        id: alloc.run(),
        text: buf,
        ...(time === null ? {} : { time, timeKind: 'word' as const, timeOrigin: 'file' as const }),
        style,
        startsWord: true,
      }),
    );
    buf = '';
    runStart = null;
  };

  const flushLine = (): void => {
    flushRun(null);
    // A stamp dangling at the end of a line has no run to open and must not open one on the next.
    runStart = null;
    lines.push(makeLine(runs, { breakBefore }));
    runs = [];
    breakBefore = 'hard';
  };

  const openStyle = (name: string, delta: InlineStyle): void => {
    flushRun(null);
    stack.push({ name, delta, classes: [], voice: null });
  };

  const close = (name: string): void => {
    flushRun(null);
    for (let i = stack.length - 1; i >= 0; i--) {
      if ((stack[i] as OpenTag).name === name) {
        stack.splice(i, 1);
        return;
      }
    }
    // A stray closer with no opener. Real files have them; ignoring it is what every renderer
    // does, and it is not worth a diagnostic slot of its own.
  };

  /** Text goes to the ruby READING while an `<rt>` is open, and to the run otherwise. One
   *  place, so the redirection cannot be got right in three sites and wrong in the fourth. */
  const emit = (s: string): void => {
    if (inRt) rtBuf += s;
    else buf += s;
  };

  let i = 0;
  while (i < text.length) {
    const ch = text[i] as string;

    if (ch === '\n') {
      if (inRt) closeRt();
      flushLine();
      i++;
      continue;
    }

    if (ch === '{' && caps.assTags) {
      const close$ = text.indexOf('}', i);
      if (close$ > i) {
        const tag = text.slice(i, close$ + 1);
        const am = ALIGN_RE.exec(tag);
        if (am !== null) {
          alignment = Number(am[1]) as Alignment;
          i = close$ + 1;
          continue;
        }
        const tm = TOGGLE_RE.exec(tag);
        if (tm !== null) {
          const on = tm[2] === '1';
          const key = tm[1] === 'i' ? 'italic' : tm[1] === 'b' ? 'bold' : 'underline';
          if (on) openStyle(`ass-${key}`, { [key]: true } as InlineStyle);
          else close(`ass-${key}`);
          i = close$ + 1;
          continue;
        }
      }
      emit(ch);
      i++;
      continue;
    }

    if (ch !== '<') {
      emit(ch);
      i++;
      continue;
    }

    const gt = text.indexOf('>', i);
    if (gt === -1) {
      emit(ch);
      i++;
      continue;
    }
    const raw = text.slice(i + 1, gt);
    const consumed = gt + 1;
    const handled = handleTag(raw);
    if (!handled) {
      // Not one of ours — literal text, exactly as a renderer treats it.
      emit(ch);
      i++;
      continue;
    }
    i = consumed;
  }

  // Trailing state: an unclosed `<rt>` or a dangling ruby is a malformed file, not a crash.
  if (inRt && rubyBase !== null) closeRt();
  flushRun(null);
  if (runs.length > 0 || lines.length === 0) lines.push(makeLine(runs, { breakBefore }));

  return { lines, alignment, wordTimed, voices, unknown };

  /*── tag dispatch ──────────────────────────────────────────────────────────────*/

  function handleTag(raw: string): boolean {
    const body = raw.trim();
    if (body === '') return false;

    // `<00:00:01.000>` — a WebVTT cue timestamp. Checked FIRST because `<1:23.4>` would
    // otherwise be read as an unknown element name.
    if (caps.timestamps && TS_RE.test(body)) {
      const at = input.parseTimestamp?.(body) ?? null;
      if (at === null) {
        unknown.push(`<${raw}>`);
        return false;
      }
      // The timestamp ENDS the previous run and STARTS the next.
      flushRun(at);
      runStart = at;
      wordTimed = true;
      return true;
    }

    const closing = body.startsWith('/');
    const name = (closing ? body.slice(1) : body).split(/[\s.]/, 1)[0]?.toLowerCase() ?? '';

    switch (name) {
      case 'i':
      case 'em':
        toggle(closing, 'i', { italic: true });
        return true;
      case 'b':
      case 'strong':
        toggle(closing, 'b', { bold: true });
        return true;
      case 'u':
        toggle(closing, 'u', { underline: true });
        return true;
      case 's':
      case 'strike':
      case 'del':
        toggle(closing, 's', { strikeOut: true });
        return true;

      case 'br':
        if (!caps.br) return false;
        flushLine();
        return true;

      case 'font': {
        if (!caps.font) return false;
        if (closing) {
          close('font');
          return true;
        }
        openStyle('font', fontDelta(body));
        return true;
      }

      case 'c': {
        if (!caps.classes) return false;
        if (closing) {
          close('c');
          return true;
        }
        flushRun(null);
        stack.push({ name: 'c', ...colorOfClasses(classesOf(body)), voice: null });
        return true;
      }

      case 'v': {
        if (!caps.voice) return false;
        if (closing) {
          close('v');
          return true;
        }
        const who = body
          .slice(1)
          .replace(/^[\s.]+/, '')
          .trim();
        if (who !== '' && !voices.includes(who)) voices.push(who);
        flushRun(null);
        stack.push({ name: 'v', delta: {}, classes: classesOf(body), voice: who });
        return true;
      }

      case 'lang': {
        if (!caps.classes) return false;
        // `<lang en>` carries a language, which this model records per TRACK rather than per
        // run. Consuming it keeps the tag out of the text; the language is not invented.
        if (closing) close('lang');
        else {
          flushRun(null);
          stack.push({ name: 'lang', delta: {}, classes: [], voice: null });
        }
        return true;
      }

      case 'ruby': {
        if (!caps.ruby) return false;
        if (closing) {
          if (inRt) closeRt();
          return true;
        }
        flushRun(null);
        return true;
      }

      case 'rt': {
        if (!caps.ruby) return false;
        if (closing) {
          closeRt();
          return true;
        }
        // The text collected so far is the ruby BASE; what follows is its reading.
        flushRun(null);
        rubyBase = runs.length > 0 ? (runs[runs.length - 1] as TextRun) : null;
        inRt = true;
        rtBuf = '';
        return true;
      }

      default:
        unknown.push(`<${raw}>`);
        return false;
    }
  }

  /**
   * A `<c>` tag's colour class becomes the tag's `primaryColor`; the rest stay classes.
   *
   * ONLY WHEN THE TAG HAS EXACTLY ONE. `<c.red.yellow>` puts two colour rules on one element and
   * which wins is a cascade-order question; the class list answers it for every renderer, and a
   * guess here would not. Such a tag keeps its classes and round-trips byte for byte.
   */
  function colorOfClasses(classes: readonly string[]): {
    delta: InlineStyle;
    classes: readonly string[];
  } {
    const map = input.classColors;
    if (map === undefined) return { delta: {}, classes };
    const colored = classes.filter((n) => map.has(n));
    const name = colored[0];
    if (colored.length !== 1 || name === undefined) return { delta: {}, classes };
    return {
      delta: { primaryColor: map.get(name) as Color },
      classes: classes.filter((n) => n !== name),
    };
  }

  function toggle(closing: boolean, key: string, delta: InlineStyle): void {
    if (closing) close(key);
    else openStyle(key, delta);
  }

  function closeRt(): void {
    if (rubyBase !== null) {
      const annotation: Annotation = { kind: 'ruby', text: rtBuf, placement: 'above' };
      const at = runs.lastIndexOf(rubyBase);
      if (at >= 0) runs[at] = { ...rubyBase, annotation };
    }
    inRt = false;
    rubyBase = null;
    rtBuf = '';
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Attribute helpers
───────────────────────────────────────────────────────────────────────────────────────*/

const ATTR_RE = /([a-zA-Z-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/g;

/** `<font color="#ff0000" face="Arial" size="14">` → the inline delta it stands for. */
export function fontDelta(body: string): InlineStyle {
  const out: Record<string, unknown> = {};
  ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null = ATTR_RE.exec(body);
  while (m !== null) {
    const key = (m[1] as string).toLowerCase();
    const value = m[3] ?? m[4] ?? m[5] ?? '';
    if (key === 'color') {
      const c: Color | null = parseCssColor(value);
      if (c !== null) out['primaryColor'] = c;
    } else if (key === 'face') {
      out['fontName'] = value;
    } else if (key === 'size') {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) out['fontSize'] = n;
    }
    m = ATTR_RE.exec(body);
  }
  return out as InlineStyle;
}

/** `c.yellow.bg_blue` → ['yellow', 'bg_blue']. Verbatim: they are the only styling carrier
 *  WebVTT and SAMI have, and normalising them would break round trip. */
export function classesOf(body: string): readonly string[] {
  const head = body.split(/\s/, 1)[0] ?? '';
  const parts = head
    .split('.')
    .slice(1)
    .filter((p) => p !== '');
  return parts;
}

/** Decodes entities across an already-tokenized run set. Applied after tokenizing so that
 *  `&lt;i&gt;` stays literal text rather than becoming a tag on a second pass.
 *
 *  THE RUBY READING IS DECODED TOO. It is entity-encoded on the wire exactly like base text but it
 *  does not live in `run.text`, so decoding runs alone left `<rt>a&amp;b</rt>` as the literal
 *  `a&amp;b`; the writer then escaped that again and every round trip corrupted it a little
 *  further (`a&amp;amp;b`, `a&amp;amp;amp;b`, …). Found by round-tripping the writer's new ruby
 *  emission, which is the test that had never existed. */
export function decodeRuns(lines: readonly TextLine[]): readonly TextLine[] {
  let changed = false;
  const out = lines.map((line) => {
    let lineChanged = false;
    const runs = line.runs.map((r) => {
      if (r.kind === 'drawing') return r;
      let next = r;
      const decoded = decodeEntities(r.text);
      if (decoded !== r.text) next = { ...next, text: decoded };
      if (r.annotation !== null) {
        const reading = decodeEntities(r.annotation.text);
        if (reading !== r.annotation.text) {
          next = { ...next, annotation: { ...r.annotation, text: reading } };
        }
      }
      if (next === r) return r;
      lineChanged = true;
      changed = true;
      return next;
    });
    return lineChanged ? { ...line, runs } : line;
  });
  return changed ? out : lines;
}

/** Ticks helper re-export so parsers do not each import the brand caster for one call. */
export const ticks = T.ticks;
